import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMediaContext } from '.';
import { getScripts } from '../../api/queue-control';
import { useSelector } from '../../hook-utils';
import { Media, Region, ScriptFolder } from '../../model';
import { telephoneNumberToNational, telephoneNumberToRegional } from '../../phone-utils';
import { actions } from '../../store';
import { MyData, selectMyData, selectMyRegion } from '../../store/reducers/config';
import { selectMedia, selectMediaNotes } from '../../store/reducers/media';
import DropdownButton from '../DropdownButton';
import { ReactComponent as PasteIcon } from '../icons/paste.svg';
import { MenuItem } from '../Menu';
import { TabButton, TabsHeader } from '../Tabs';
import './MediaPage.scss';

interface Props {
  mediaId: string
}

export default function MediaPage({ mediaId }: Props) {
  const mediaNotes = useSelector(s => selectMediaNotes(s, mediaId))

  const dispatch = useDispatch()
  const saveNotes = useCallback(() => {
    dispatch(actions.saveMediaNotes(mediaId))
  }, [dispatch, mediaId])

  useEffect(() => {
    return () => { saveNotes() }
  }, [saveNotes])

  return <div className='MediaPage'>
    <h2>Notes</h2>
    <textarea autoFocus className='callNotes' onChange={e => {
      dispatch(actions.setMediaNotes(mediaId, e.currentTarget.value))
    }} value={mediaNotes} onBlur={saveNotes} />
    <ScriptsSection mediaId={mediaId} />
  </div>
}

function ScriptsSection({ mediaId }: { mediaId: string }) {
  const media = useSelector(s => selectMedia(s, mediaId))

  const [tab, setTab] = useState(0)
  const [item, setItem] = useState(0)
  const [scripts, setScripts] = useState<ScriptFolder[]>()
  const region = useSelector(selectMyRegion)
  const config = useSelector(selectMyData)
  const ref = useRef<HTMLDivElement>(null)
  const { appendScript, canAppendScript } = useMediaContext()

  useEffect(() => {
    if (!media || !media.isQueueCall) return

    let mounted = true
    load()
    return () => { mounted = false }

    async function load() {
      if (!media) return

      const data = await getScripts(media.id, media.dnis, media.clid)
      if (!mounted) { return }
      setScripts(data)
    }
  }, [media])

  if (!scripts || !media || !media.isQueueCall) { return null }

  const script = scripts[tab]
  const selected = script && script.items[item]
  const html = selected && config && replaceScriptingVars(selected.html, media, config, region)

  function onSwitchTab(index: number) {
    setTab(index)
    setItem(0)
  }

  const canApply =
    media.mediaType === 'TC' && canAppendScript ||
    media.mediaType === 'EMAIL' && canAppendScript
  const mediaType = media.mediaType

  function applyScript() {
    if (!html) { return }
    const sel = window.getSelection()
    const node = ref.current

    let text = sel && node && node.contains(sel.anchorNode) ? sel.toString() : null

    if (mediaType === 'TC') {
      appendScript(text || html)
    } else if (mediaType === 'EMAIL') {
      appendScript(text || html)
    }
  }

  if (scripts.length === 0 || !media) return null

  return <>
    <h1 className='ScriptsHeader'>
      Scripts
      {canApply && <button onClick={applyScript}><PasteIcon height='100%' /></button>}
    </h1>
    <div className='ScriptsSection'>
      <TabsHeader className='tabsHeader'>
        {scripts.map((s, i) => <TabButton key={i} active={i === tab} onClick={() => onSwitchTab(i)}>{s.name}</TabButton>)}
      </TabsHeader>
      {script && <div className='tabContent'>
        <DropdownButton items={script.items.map((s, i) => <MenuItem key={i} onClick={() => setItem(i)}>{s.name}</MenuItem>)} setWidth>
          {selected && selected.name}
        </DropdownButton>
        {html && <div ref={ref} className='text' dangerouslySetInnerHTML={{ __html: html }} />}
      </div>}
    </div>
  </>
}

type ScriptFunc = (ctx: {
  config: MyData,
  media: Media,
  region: Region | null,
}) => string

enum NumberType {
  None = -1,
  Desk = 0,
  Queue = 1,
  SpeedDial = 2,
  Business = 3,
  Mobile = 4,
  Associate = 5,
  Home = 6,
  Other = 7,
  Email = 8,
}

const vars: Record<string, ScriptFunc> = {
  CLID_Description: ({ media }) => media.clidDescription,
  CLID_NumberE164: ({ media }) => media.mediaType !== 'CALL' && media.mediaType !== 'VM' ? '' : media.clid,
  CLID_NumberNational: ({ media, region }) => media.mediaType !== 'CALL' && media.mediaType !== 'VM' ? '' : telephoneNumberToNational(region, media.clid),
  CLID_NumberRegional: ({ media, region }) => media.mediaType !== 'CALL' && media.mediaType !== 'VM' ? '' : telephoneNumberToRegional(region, media.clid),
  CLID_AccountCode: ({ media }) => media.clidAccountCode,
  CLID_NumberType: ({ media }) => NumberType[media.clidNumberType],
  Queue_Description: ({ media }) => media.dnisDescription,
  Queue_Number: ({ media }) => media.dnis,
  Agent_Name: ({ config }) => config.fullName,
  Agent_FirstName: ({ config }) => config.firstname,
  Agent_LastName: ({ config }) => config.lastname,
  Agent_ExtensionNumber: ({ config }) => config.extension,
}

export function replaceScriptingVars(text: string, media: Media, config: MyData, region: Region | null) {
  const ctx = { media, config, region }
  for (const key in vars) {
    text = text.replace(new RegExp(`%${key}%`, 'g'), vars[key](ctx))
  }
  return text
}
